import { useGetOrganizationById, UseGetOrganizationByIdReturnData } from "@/features/organization/hooks/useQuery/useGetOrganizationById";
import { useOrganizationStore, useUserStore } from "@/lib/zustand/useStore";
import { UseCheckOrganizationAccessProps, UseCheckOrganizationAccessReturn } from "./useCheckOrganizationAccess.types";

export function useCheckOrganizationAccess({
    orgId,
    userId,
    requiredPermissions,
    any = false,
    roles
}: UseCheckOrganizationAccessProps): UseCheckOrganizationAccessReturn {
    const { organization: storeOrganization } = useOrganizationStore();
    const storeUser = useUserStore();
    
    let organization: UseGetOrganizationByIdReturnData | null | undefined = storeOrganization;
    const { data: fetchedOrganization } = useGetOrganizationById(orgId);
    
    if (orgId) {
        organization = fetchedOrganization;
    }
    
    if (!organization) {
        return {
            hasAccess: false,
            userPermissions: {},
        };
    }
    
    if (!userId) {
        userId = storeUser.id ?? undefined;
    }
    
    const userMember = organization?.organisation_members?.find(m => m.user_id === userId);
    const userRole = userMember?.role;
    const userPermissions = userRole || {};

    // Check if user is a member of the organization
    if (!userMember) {
        return {
            hasAccess: false,
            userPermissions: {},
        };
    }

    if (roles) {
        const requiredRoles = Array.isArray(roles) ? roles : [roles];
        const userRoleType = userRole?.role; // Assuming role has a 'role' field with the role type
        if(!userRoleType) {
            return {
                hasAccess: false,
                userPermissions,
            };
        }
        const hasRequiredRole = requiredRoles.includes(userRoleType);
        
        if (!hasRequiredRole) {
            return {
                hasAccess: false,
                userPermissions,
            };
        }
    }

    const requiredPermissionEntries = Object.entries(requiredPermissions).filter(([, value]) => value === true);
    
    if (requiredPermissionEntries.length === 0) {
        return {
            hasAccess: true,
            userPermissions,
        };
    }

    const hasAccess = any 
        ? requiredPermissionEntries.some(([key]) => {
            return userPermissions[key as keyof typeof userPermissions] === true;
          })
        : requiredPermissionEntries.every(([key]) => {
            return userPermissions[key as keyof typeof userPermissions] === true;
          });

    return {
        hasAccess,
        userPermissions,
    };
}